import { useMemo } from 'react';
import { TreeType, TreeViewBy } from '../types';
import {
  TREE_NODE,
  TREE_TYPE,
  TREE_VIEW_BY,
  VIEW_ID,
} from '../constants/urlParams';
import useSearchParams from './useSearchParams';
import useAccount from './useAccount';
import useHasDbtProjects from './useHasDbtProjects';

type TreeViewParamsReturnValue = {
  treeViewBy: TreeViewBy;
  defaultTreeViewBy: TreeViewBy;
  hasTreeViewByParam: boolean;
  treeType: TreeType;
  setTreeViewBy: (value: string) => void;
  setTreeType: (value: string) => void;
  deleteTreeViewBy: () => void;
  deleteTreeType: () => void;
  urlTreeNode: string | null;
  urlSavedView: string | null;
};

const useTreeViewParams = (): TreeViewParamsReturnValue => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: account } = useAccount();
  const hasDbtProjects = useHasDbtProjects();

  const treeType = useMemo(
    () => searchParams.get(TREE_TYPE) || 'data_assets',
    [searchParams],
  );

  const defaultTreeViewBy: TreeViewBy =
    treeType === 'data_assets'
      ? !hasDbtProjects || account?.default_asset_tree_view_dwh
        ? 'dwh'
        : 'dbt'
      : 'paths';

  const hasTreeViewByParam = searchParams.get(TREE_VIEW_BY) != null;

  const treeViewBy = useMemo(
    () => searchParams.get(TREE_VIEW_BY) || defaultTreeViewBy,
    [searchParams, defaultTreeViewBy],
  );

  const urlTreeNode = useMemo(
    () => searchParams.get(TREE_NODE),
    [searchParams],
  );

  const urlSavedView = useMemo(() => searchParams.get(VIEW_ID), [searchParams]);

  const setTreeViewBy = (value: string) => {
    searchParams.set(TREE_VIEW_BY, value);
    setSearchParams(searchParams);
  };

  const deleteTreeViewBy = () => {
    searchParams.delete(TREE_VIEW_BY);
    setSearchParams(searchParams);
  };

  const setTreeType = (value: string) => {
    searchParams.set(TREE_TYPE, value);
    setSearchParams(searchParams);
  };

  const deleteTreeType = () => {
    searchParams.delete(TREE_TYPE);
    setSearchParams(searchParams);
  };

  return {
    defaultTreeViewBy,
    hasTreeViewByParam,
    treeViewBy: treeViewBy as TreeViewBy,
    treeType: treeType as TreeType,
    setTreeViewBy,
    setTreeType,
    deleteTreeViewBy,
    deleteTreeType,
    urlTreeNode,
    urlSavedView,
  };
};

export default useTreeViewParams;
